import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Monitor, DeviceTablet, DeviceMobile } from "@phosphor-icons/react";
import { PlatformMockup } from "@/components/PlatformMockup";
import { renderRich } from "@/lib/richText";
import { useApi } from "@/api/useApi";

// Best-effort — dimensions/health-status enrich the Full Content tab when the asset is a saved
// Media Library item; freshly-generated (not-yet-saved) media has no id yet, so this silently
// no-ops rather than blocking the rest of the preview.
function useMediaDetails(mediaId) {
  const api = useApi();
  const [details, setDetails] = useState(null);
  useEffect(() => {
    setDetails(null);
    if (!mediaId) return;
    let alive = true;
    api.get(`/media/${mediaId}/details`).then((r) => { if (alive) setDetails(r.data); }).catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaId]);
  return details;
}

function MetaRow({ label, value, testid }) {
  if (!value) return null;
  return <p className="text-[#7A9090] text-xs" data-testid={testid}><span className="font-semibold text-[#3C4048]">{label}:</span> {value}</p>;
}

function TabButton({ active, onClick, children, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${active ? "bg-[#3A7A8C] text-white border-[#3A7A8C]" : "bg-white text-[#3C4048] border-[#e5e7eb] hover:border-[#5FA8B4]"}`}>
      {children}
    </button>
  );
}

function SocialFullPreview({ data }) {
  const [tab, setTab] = useState("platform"); // platform (default, approximate) | full (authoritative)
  const details = useMediaDetails(data.mediaId);
  const dims = details?.width ? `${details.width} × ${details.height}px` : null;
  return (
    <div className="flex flex-col gap-4" data-testid="full-preview-social">
      <div className="flex items-center gap-2" data-testid="full-preview-tabs">
        <TabButton active={tab === "platform"} onClick={() => setTab("platform")} testid="full-preview-tab-platform">Platform View</TabButton>
        <TabButton active={tab === "full"} onClick={() => setTab("full")} testid="full-preview-tab-full">Full Content</TabButton>
      </div>

      {tab === "platform" ? (
        <div className="flex justify-center bg-[#ECEEEA] rounded-[16px] p-6" data-testid="full-preview-platform-view">
          <PlatformMockup platform={data.platform} mediaUrl={data.mediaUrl} mediaKind={data.mediaKind}
            caption={data.caption} brandName={data.brandName} brandLogoUrl={data.brandLogoUrl} />
          <p className="sr-only">Approximate collapsed presentation — see Full Content for the complete, uncropped media and caption.</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="full-preview-full-content">
          {data.mediaUrl && (
            <div className="bg-black/5 rounded-[12px] flex items-center justify-center p-2">
              {data.mediaKind === "video" ? (
                <video src={data.mediaUrl} controls className="max-h-[55vh] w-auto max-w-full object-contain rounded-[8px]" data-testid="full-preview-video" />
              ) : (
                <img src={data.mediaUrl} alt="" className="max-h-[55vh] w-auto max-w-full object-contain rounded-[8px]" data-testid="full-preview-image" />
              )}
            </div>
          )}
          <div className="bg-[#fafafa] border border-[#e5e7eb] rounded-[12px] p-4 text-[#3C4048] text-sm whitespace-pre-line" data-testid="full-preview-caption">
            {renderRich(data.caption) || <span className="text-[#a3b0b0] italic">No caption</span>}
          </div>
          <div className="grid grid-cols-2 gap-1">
            <MetaRow label="Platform" value={data.platform} testid="full-preview-meta-platform" />
            <MetaRow label="Account" value={data.brandName} testid="full-preview-meta-account" />
            <MetaRow label="Media type" value={data.mediaUrl ? (data.mediaKind || "image") : "text only"} testid="full-preview-meta-kind" />
            <MetaRow label="Dimensions" value={dims} testid="full-preview-meta-dimensions" />
          </div>
        </div>
      )}
    </div>
  );
}

function ImageFullPreview({ data }) {
  const details = useMediaDetails(data.mediaId);
  const dims = details?.width ? `${details.width} × ${details.height}px` : null;
  return (
    <div className="space-y-3" data-testid="full-preview-image-wrap">
      <div className="bg-black/5 rounded-[12px] flex items-center justify-center p-2">
        <img src={data.mediaUrl} alt={data.altText || ""} className="max-h-[65vh] w-auto max-w-full object-contain rounded-[8px]" data-testid="full-preview-image" />
      </div>
      <div className="text-[#7A9090] text-xs space-y-0.5">
        {data.filename && <p className="text-[#3C4048] font-semibold text-sm" data-testid="full-preview-filename">{data.filename}</p>}
        {(dims || details?.file_size) && (
          <p data-testid="full-preview-dimensions">{dims || ""}{details?.file_size ? `${dims ? " · " : ""}${(details.file_size / 1024).toFixed(0)} KB` : ""}</p>
        )}
        {data.altText && <p className="italic" data-testid="full-preview-alt-text">Alt text: "{data.altText}"</p>}
      </div>
    </div>
  );
}

function VideoFullPreview({ data }) {
  return (
    <div className="space-y-3" data-testid="full-preview-video-wrap">
      <div className="bg-black rounded-[12px] flex items-center justify-center p-2">
        <video src={data.mediaUrl} controls poster={data.posterUrl} className="max-h-[65vh] w-auto max-w-full object-contain rounded-[8px]" data-testid="full-preview-video" />
      </div>
      {data.caption && <p className="text-[#3C4048] text-sm whitespace-pre-line" data-testid="full-preview-video-caption">{data.caption}</p>}
      {data.filename && <p className="text-[#7A9090] text-xs" data-testid="full-preview-filename">{data.filename}</p>}
    </div>
  );
}

function NewsletterFullPreview({ data }) {
  const [viewport, setViewport] = useState("desktop");
  const widths = { desktop: "100%", tablet: "768px", mobile: "390px" };
  return (
    <div className="space-y-3" data-testid="full-preview-newsletter">
      <div className="flex items-center gap-2" data-testid="full-preview-viewport-toggle">
        <TabButton active={viewport === "desktop"} onClick={() => setViewport("desktop")} testid="full-preview-viewport-desktop">
          <span className="inline-flex items-center gap-1.5"><Monitor size={14} /> Desktop</span>
        </TabButton>
        <TabButton active={viewport === "tablet"} onClick={() => setViewport("tablet")} testid="full-preview-viewport-tablet">
          <span className="inline-flex items-center gap-1.5"><DeviceTablet size={14} /> Tablet</span>
        </TabButton>
        <TabButton active={viewport === "mobile"} onClick={() => setViewport("mobile")} testid="full-preview-viewport-mobile">
          <span className="inline-flex items-center gap-1.5"><DeviceMobile size={14} /> Mobile</span>
        </TabButton>
      </div>
      <p className="text-[10px] text-[#9AA6AC]">Browser viewport preview — approximates Gmail/Apple Mail; Outlook desktop always shows the Desktop layout regardless of width.</p>
      <div className="flex justify-center bg-[#ECEEEA] rounded-[16px] p-4 overflow-auto max-h-[70vh]">
        <iframe
          data-testid="full-preview-newsletter-frame"
          title={`Full preview — ${data.subject || "newsletter"}`}
          srcDoc={data.html}
          sandbox=""
          style={{ width: widths[viewport], height: "68vh" }}
          className="rounded-[10px] border border-[#e5e7eb] bg-white transition-[width] duration-200"
        />
      </div>
    </div>
  );
}

// Universal Full Preview — the authoritative "inspect everything before you approve/use it" view.
// type: "image" | "video" | "social" | "newsletter". Never mutates content — read-only by design.
export function FullPreviewModal({ open, onOpenChange, preview }) {
  if (!preview) return null;
  const title = preview.title || (preview.type === "social" ? `${preview.data.platform || ""} post`.trim() : preview.type === "newsletter" ? preview.data.subject : "Full Preview");
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="full-preview-modal" className="bg-white border-[#e5e7eb] max-w-3xl w-[92vw] max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#3C4048] truncate pr-6" style={{ fontFamily: "Montserrat", fontWeight: 800 }} data-testid="full-preview-title">{title}</DialogTitle>
          <DialogDescription className="sr-only">Complete, uncropped inspection of this content before you approve or use it.</DialogDescription>
        </DialogHeader>
        {preview.type === "social" && <SocialFullPreview data={preview.data} />}
        {preview.type === "image" && <ImageFullPreview data={preview.data} />}
        {preview.type === "video" && <VideoFullPreview data={preview.data} />}
        {preview.type === "newsletter" && <NewsletterFullPreview data={preview.data} />}
      </DialogContent>
    </Dialog>
  );
}
